import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';

export interface OrgListEntry {
  orgId: string;
  name: string;
  kind: 'personal' | 'team';
  domain: string | null;
  role: 'admin' | 'member' | 'viewer';
  createdAt: string;
}

interface OrgContextValue {
  orgs: OrgListEntry[];
  currentOrg: OrgListEntry | null;
  isLoading: boolean;
  refetch: () => void;
  /** Set the active org. Persists to localStorage and updates context
   *  immediately so global routes (no :orgId in URL) reflect the change
   *  without a navigation. Org-scoped routes should also navigate. */
  selectOrg: (orgId: string) => void;
}

const OrgContext = createContext<OrgContextValue>({
  orgs: [],
  currentOrg: null,
  isLoading: true,
  refetch: () => {},
  selectOrg: () => {},
});

const LAST_ORG_KEY = 'lastOrgId';

function readLastOrgId(): string | null {
  try { return localStorage.getItem(LAST_ORG_KEY); } catch { return null; }
}
function writeLastOrgId(id: string): void {
  try { localStorage.setItem(LAST_ORG_KEY, id); } catch { /* ignore */ }
}

/**
 * OrgProvider supplies the user's org list (for the switcher) plus the
 * "current" org. Mount inside the AuthGuard so the user is known to be
 * authenticated. The auth middleware on the API will lazy-create a
 * personal org on first request — so by the time /me/orgs returns,
 * every authenticated user has at least one org.
 *
 * Resolution order for `currentOrg` (layered):
 *   1. URL `:orgId` param — if present and matches a known org. Wins
 *      so deep-links and explicit navigation are always authoritative.
 *   2. `selectedOrgId` state — set when the user picks an org from the
 *      switcher. Lets switching work even on global routes (/settings,
 *      /learn, /notifications) where the URL has no orgId to update.
 *   3. localStorage `lastOrgId` — initial seed for `selectedOrgId` so
 *      a returning user lands on the same org across reloads.
 *   4. First org in the user's list — fresh sign-in fallback.
 *
 * Whatever resolves to `currentOrg` is mirrored back to localStorage so
 * subsequent global-route visits / reloads pick the same one.
 */
export function OrgProvider({ children }: { children: ReactNode }) {
  const { orgId: urlOrgId } = useParams<{ orgId?: string }>();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['me', 'orgs'],
    queryFn: () => apiClient.listMyOrgs(),
    staleTime: 60_000,
  });

  const orgs = (data?.data as OrgListEntry[] | undefined) ?? [];

  // Initialised from localStorage on mount; mutated by `selectOrg`.
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() => readLastOrgId());

  // Layered resolution. Each step short-circuits on a hit.
  const fromUrl = urlOrgId ? orgs.find((o) => o.orgId === urlOrgId) : null;
  const fromState = !fromUrl ? orgs.find((o) => o.orgId === selectedOrgId) : null;
  const fallback = !fromUrl && !fromState ? (orgs[0] ?? null) : null;
  const currentOrg = fromUrl ?? fromState ?? fallback;

  // Persist whatever resolved as the current org so the next global-
  // route visit / reload picks the same one. Also keeps `selectedOrgId`
  // in sync when navigation drove the change (e.g. user clicked a
  // /orgs/<id>/... link directly).
  useEffect(() => {
    if (currentOrg) {
      writeLastOrgId(currentOrg.orgId);
      if (selectedOrgId !== currentOrg.orgId) setSelectedOrgId(currentOrg.orgId);
    }
  }, [currentOrg?.orgId, selectedOrgId]);

  const selectOrg = useCallback((orgId: string) => {
    writeLastOrgId(orgId);
    setSelectedOrgId(orgId);
  }, []);

  return (
    <OrgContext.Provider value={{ orgs, currentOrg, isLoading, refetch, selectOrg }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrgContext(): OrgContextValue {
  return useContext(OrgContext);
}

/** Read scoped IDs from the current URL — handy for queries inside
 * project-scoped pages. Throws if expected params are missing so the
 * caller doesn't silently fall through with `undefined`. */
export function useScopedParams(): {
  orgId: string;
  portfolioId: string;
  projectId: string;
} {
  const { orgId, portfolioId, projectId } = useParams<{
    orgId?: string;
    portfolioId?: string;
    projectId?: string;
  }>();
  if (!orgId || !portfolioId || !projectId) {
    throw new Error('useScopedParams: missing one of orgId/portfolioId/projectId in URL');
  }
  return { orgId, portfolioId, projectId };
}
